import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import StandardModal from "../shared/Modal/StandardModal";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";
import { IconTrash } from "@tabler/icons-react";
import {
  TextInput,
  Button,
  Group,
  Stack,
  Checkbox,
  Select,
  Text,
  Alert,
} from "@mantine/core";

function EditGroupModal({
  isOpen,
  onClose,
  onGroupUpdated,
  onGroupDeleted,
  group,
  repTypes = [],
  loadingRepTypes = false,
}) {
  const { getVerifiedJWT } = useAuth();
  const [saving, setSaving] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [enableChat, setEnableChat] = useState(false);
  const [autoAdd, setAutoAdd] = useState(false);
  const [selectedRepType, setSelectedRepType] = useState("");
  const [nameError, setNameError] = useState("");
  const [autoAddError, setAutoAddError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [initialGroupState, setInitialGroupState] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Reset form when modal opens or group changes
  useEffect(() => {
    if (isOpen && group) {
      setGroupName(group.name || "");
      setEnableChat(group.enable_chat || false);
      setAutoAdd(group.auto_add || false);
      setSelectedRepType(group.rep_type_id ? group.rep_type_id.toString() : "");
      setNameError("");
      setAutoAddError("");
      setSaveError("");

      // Store initial state to check what was previously set
      setInitialGroupState({
        enable_chat: group.enable_chat || false,
        auto_add: group.auto_add || false,
        include_downline: group.include_downline || false,
        rep_type_id: group.rep_type_id || null,
      });
    }
  }, [isOpen, group]);

  const updateGroup = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(getApiEndpoint(`/v1/groups/group`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          group_id: group.id,
          name: groupName.trim(),
          enable_chat: enableChat,
          auto_add: autoAdd,
          ...(autoAdd && selectedRepType && { rep_type_id: selectedRepType }),
        }),
      });

      if (response.ok) {
        const result = await response.json();
        return result.data;
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to update group. Please try again."
        );
      }
    } catch (error) {
      throw new Error("Failed to update group. Please try again.");
    }
  };

  const handleSave = async () => {
    // Clear previous errors
    setNameError("");
    setAutoAddError("");
    setSaveError("");

    // Validate group name
    if (!groupName.trim()) {
      setNameError("Group name is required");
      return;
    }

    // Validate auto add requirements
    if (autoAdd && !selectedRepType) {
      setAutoAddError("Please select a rep type");
      return;
    }

    try {
      setSaving(true);

      // Close modal immediately for instant feedback (optimistic update)
      onClose();

      // Update in background
      updateGroup()
        .then(() => {
          // Refresh the list after backend confirms
          onGroupUpdated();
        })
        .catch(error => {
          // If error occurs after modal is closed, could show a toast/notification

          // Re-open modal with error if needed
          setSaveError(error.message);
        })
        .finally(() => {
          setSaving(false);
        });
    } catch (error) {
      // Handle synchronous errors
      setSaveError(error.message);
      setSaving(false);
    }
  };

  const handleAutoAddChange = checked => {
    setAutoAdd(checked);
    if (!checked) {
      setSelectedRepType("");
      setAutoAddError("");
    }
  };

  const handleDelete = async () => {
    if (!group || isDeleting) return;

    try {
      setIsDeleting(true);
      setDeleteError("");

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setDeleteError("Authentication failed");
        return;
      }

      const response = await fetch(
        getApiEndpoint(`/v1/groups/group/${group.id}`),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        setShowDeleteConfirm(false);
        if (onGroupDeleted) {
          onGroupDeleted();
        }
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setDeleteError(errorData.message || "Failed to delete group");
      }
    } catch (err) {
      setDeleteError("Failed to delete group. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Convert rep types to Select options format
  const repTypeOptions = repTypes.map(repType => ({
    value: repType.id.toString(),
    label: repType.name,
  }));

  return (
    <>
      <StandardModal
        isOpen={isOpen}
        onClose={onClose}
        title="EDIT GROUP"
        footerLeft={<div></div>}
        footerRight={<div></div>}
      >
        <Stack gap="md">
          <TextInput
            label="GROUP NAME"
            value={groupName}
            onChange={event => {
              setGroupName(event.currentTarget.value);
              setNameError(""); // Clear error when user types
              setSaveError(""); // Clear save error when user types
            }}
            placeholder="Enter group name"
            disabled={saving}
            error={nameError}
            required
          />

          <Text size="sm" c="dimmed">
            These settings once enabled can't be disabled.
          </Text>

          <Checkbox
            label="Enable Chat"
            checked={enableChat}
            onChange={event => setEnableChat(event.currentTarget.checked)}
            disabled={
              saving || (initialGroupState && initialGroupState.enable_chat)
            }
          />

          <Checkbox
            label="Auto Add By Rep Type"
            checked={autoAdd}
            onChange={event => handleAutoAddChange(event.currentTarget.checked)}
            disabled={
              saving || (initialGroupState && initialGroupState.auto_add)
            }
          />

          {autoAdd && (
            <Stack gap="md">
              <Select
                label="REP TYPE"
                placeholder={
                  loadingRepTypes ? "Loading rep types..." : "Select rep type"
                }
                data={repTypeOptions}
                value={selectedRepType}
                onChange={value => {
                  setSelectedRepType(value);
                  setAutoAddError(""); // Clear error when user selects
                  setSaveError(""); // Clear save error when user selects
                }}
                disabled={
                  saving ||
                  loadingRepTypes ||
                  (initialGroupState && initialGroupState.auto_add)
                }
                searchable
                clearable
                nothingFoundMessage={
                  repTypes.length === 0 && !loadingRepTypes
                    ? "No rep types available"
                    : "No options found"
                }
              />

              {autoAddError && (
                <DisplayMessage type="error" message={autoAddError} />
              )}
            </Stack>
          )}

          {saveError && <DisplayMessage type="error" message={saveError} />}

          {showDeleteConfirm && (
            <Alert variant="light" color="red" mt="md">
              <Stack gap="sm">
                <Text fw={600}>
                  Are you sure you want to delete "{group?.name}"?
                </Text>
                <Text size="sm" c="dimmed">
                  This action cannot be undone. All members will be removed from
                  the group and any associated data will be lost.
                </Text>
                {deleteError && (
                  <DisplayMessage type="error" message={deleteError} />
                )}
                <Group justify="flex-end" gap="sm">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteError("");
                    }}
                    disabled={isDeleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="red"
                    size="sm"
                    onClick={handleDelete}
                    loading={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Delete Group"}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}

          <Group justify="space-between" mt="md">
            <Button
              variant="outline"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={saving || isDeleting}
            >
              Delete Group
            </Button>

            <Group gap="md">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                loading={saving}
                disabled={!groupName.trim() || (autoAdd && !selectedRepType)}
              >
                {saving ? "Updating..." : "Update Group"}
              </Button>
            </Group>
          </Group>
        </Stack>
      </StandardModal>
    </>
  );
}

export default EditGroupModal;
